import { Clock } from 'lucide-react'

import styles from './SearchDropdown.module.css'

/**
 * RecentRow — recent-search entry inside the dropdown (UI-SPEC §Components 4,
 * lines 287-292). Shape mirrors `<SearchResult />` but:
 *   - Icon swapped to `Clock`.
 *   - Secondary line shows relative time derived from `savedAt`.
 *
 * No external date-fns dep; the relative formatter is a tiny utility scoped
 * here (UI-SPEC line 291).
 */
export interface RecentRowProps {
  id: string
  label: string
  sublabel?: string
  savedAt: number
  highlighted?: boolean
  optionId?: string
  onSelect: () => void
}

function formatRelative(savedAt: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - savedAt)
  const MIN = 60_000
  const HOUR = 60 * MIN
  const DAY = 24 * HOUR
  if (diff < MIN) return 'just now'
  if (diff < HOUR) return `${Math.floor(diff / MIN)}m ago`
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`
  const days = Math.floor(diff / DAY)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

export function RecentRow({
  label,
  sublabel,
  savedAt,
  highlighted,
  optionId,
  onSelect,
}: RecentRowProps) {
  const classes = [styles.row, highlighted ? styles.highlighted : '']
    .filter(Boolean)
    .join(' ')
  const rel = formatRelative(savedAt)
  const secondary = sublabel ? `${sublabel} · ${rel}` : rel
  return (
    <button
      type="button"
      id={optionId}
      role="option"
      aria-selected={!!highlighted}
      className={classes}
      onClick={onSelect}
    >
      <Clock size={20} strokeWidth={2} aria-hidden="true" className={styles.icon} />
      <span className={styles.text}>
        <span className={styles.primary}>{label}</span>
        <span className={styles.secondary}>{secondary}</span>
      </span>
    </button>
  )
}
